import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import Sidebar from "../features/sidebar/components/sidebar"
import HeaderSection from "../components/header-section"
import {
  DocumentTextIcon,
  UserIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
  AcademicCapIcon,
} from "@heroicons/react/24/outline"

// Measure icon mapping by name - more reliable than ID-based mapping
const MEASURE_NAME_ICON_MAP: Record<string, string> = {
  "developmental orientation": "1. Developmental Orientation.png",
  "development orientation": "1. Developmental Orientation.png", // alias
  "dependability": "2. Dependability.png",
  "innovation": "3. Innovation.png",
  "creative decisiveness": "4. Creative Decisiveness.png",
  "creative flexibility": "5. Creative Flexibility.png",
  "workplace engagement": "6. Workplace Engagement.png",
  "people centrism": "7. People Centrism.png",
  "communication": "8. Communication.png",
  "empathetic communication": "9. Empathetic Communication.png",
  "collaboration": "10. Collaboration.png",
  "team orientation": "11 Team Orientation.png",
  "conflict approach": "12 Conflit Approach.png",
  "conflict response": "13. Conflict Response.png",
  "conflict management": "14. Conflict Management.png",
  "conflict resolution": "14. Conflict Management.png", // alias
  "emotional resilience": "15. Emotional Resilience.png",
  "breadth of approach": "16. Breadth of Approach.png",
  "resource management": "17. Resource Management.png",
  "crisis preparedness": "18. Crisis Preparedness.png",
  "crisis response": "19. Crisis Response.png",
  "crisis recovery": "20. Crisis Recovery.png",
  "task management": "21. Task Management.png",
  "information management": "22. Information Management.png",
  "strategic quantitative reasoning": "23. Strategic Quantitative Reasoning.png",
  "strategic quantitative thinking": "23. Strategic Quantitative Reasoning.png", // alias
  "strategic thinking": "24. Strategic Thinking.png",
  "advanced strategy": "25. Advanced Strategy.png",
  "productivity": "26. Productivity.png",
  "proactivity": "27. Proactivity.png",
  "initiative": "28. Initiative.png",
  "goal orientation": "29. Goal Orientation.png",
  "goal management": "30. Goal Management.png",
}

// Icon component for section icons - uses Heroicons for consistent platform styling
const SectionIcon: React.FC<{ sectionId: string; className?: string }> = ({ sectionId, className = "w-5 h-5" }) => {
  const iconProps = { className, strokeWidth: 1.5 }

  switch (sectionId) {
    case 'definition':
      return <DocumentTextIcon {...iconProps} />
    case 'individual':
      return <UserIcon {...iconProps} />
    case 'team':
      return <UserGroupIcon {...iconProps} />
    case 'organization':
      return <BuildingOffice2Icon {...iconProps} />
    case 'personal':
      return <AcademicCapIcon {...iconProps} />
    default:
      return <DocumentTextIcon {...iconProps} />
  }
}

// Get measure icon path by name
const getMeasureIconPathByName = (measureName: string | undefined): string => {
  if (!measureName) return `/v3/assets/measures/8. Communication.png`
  const filename = MEASURE_NAME_ICON_MAP[measureName.toLowerCase()]
  return filename ? `/v3/assets/measures/${filename}` : `/v3/assets/measures/8. Communication.png`
}

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000'

// Types for measure guide content from API
type MeasureGuideContent = {
  id: number
  measureName: string
  measureColor: string | null
  description: string | null
  guideDefinition: string | null
  guideIndividualBehavior: string | null
  guideTeamImpact: string | null
  guideOrganizationalUse: string | null
  guidePersonalDevelopment: string | null
}

// LocalStorage key for focus measure (same as microlearning-dashboard)
const FOCUS_MEASURE_KEY = "microlearning_focus_measure"

// Get saved focus measure from localStorage
const getSavedFocusMeasure = (): number | null => {
  try {
    const saved = localStorage.getItem(FOCUS_MEASURE_KEY)
    return saved ? parseInt(saved, 10) : null
  } catch {
    return null
  }
}

const MeasureGuide: React.FC = () => {
  const { measureId: urlMeasureId } = useParams<{ measureId: string }>()
  const navigate = useNavigate()
  const [activeSection, setActiveSection] = useState<string>('definition')
  const [measure, setMeasure] = useState<MeasureGuideContent | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Use URL param if provided, otherwise try localStorage
  const effectiveMeasureId = urlMeasureId ? parseInt(urlMeasureId) : getSavedFocusMeasure()

  useEffect(() => {
    const fetchMeasureGuide = async () => {
      if (!effectiveMeasureId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        const response = await axios.get(
          `${API_BASE_URL}/saas/measure-guides/measures/${effectiveMeasureId}`,
          { withCredentials: true }
        )

        if (response.data.status === 'success' && response.data.data) {
          setMeasure(response.data.data)
        } else {
          setError('Failed to load measure guide')
        }
      } catch (err) {
        console.error('Error fetching measure guide:', err)
        setError('Failed to load measure guide. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchMeasureGuide()
  }, [effectiveMeasureId])

  // Loading state
  if (loading) {
    return (
      <div className="relative bg-base-white w-full h-[100dvh] flex flex-row items-start justify-start font-text-md-regular">
        <Sidebar />
        <div className="w-full h-[100dvh] flex-1 flex flex-col">
          <HeaderSection />
          <div className="bg-gray-50 relative box-border w-full max-w-[100dvw] h-full flex-1 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4d86ff]"></div>
            <p className="mt-4 text-gray-600">Loading measure guide...</p>
          </div>
        </div>
      </div>
    )
  }

  // No measure selected or error state
  if (!effectiveMeasureId || error || !measure) {
    const noFocusMeasure = !effectiveMeasureId
    return (
      <div className="relative bg-base-white w-full h-[100dvh] flex flex-row items-start justify-start font-text-md-regular">
        <Sidebar />
        <div className="w-full h-[100dvh] flex-1 flex flex-col">
          <HeaderSection />
          <div className="bg-gray-50 relative box-border w-full max-w-[100dvw] h-full flex-1 flex flex-col items-center justify-center">
            <div className="text-center max-w-md">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {noFocusMeasure ? "Select a Focus Measure" : error ? "Error Loading Guide" : "Measure Guide Not Found"}
              </h2>
              <p className="text-gray-600 mb-6">
                {noFocusMeasure
                  ? "To view your measure guide, please select a focus measure in your microlearning dashboard first."
                  : error || "The requested measure guide could not be found."}
              </p>
              <button
                onClick={() => navigate('/microlearning/overview')}
                className="px-6 py-3 bg-[#4d86ff] text-white rounded-lg hover:bg-[#3d76ef] transition-colors font-medium"
              >
                {noFocusMeasure ? "Select Focus Measure" : "Return to Dashboard"}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const sections = [
    { id: 'definition', label: 'Definition' },
    { id: 'individual', label: 'Individual Behaviors' },
    { id: 'team', label: 'Team Impact' },
    { id: 'organization', label: 'Organizational Applications' },
    { id: 'personal', label: 'Personal Development' }
  ]

  // Get section content with fallback to description if guide content is not available
  const getSectionContent = (sectionId: string): string => {
    const fallbackText = "Guide content for this section is being developed. Check back soon for detailed insights."

    switch (sectionId) {
      case 'definition':
        return measure.guideDefinition || measure.description || fallbackText
      case 'individual':
        return measure.guideIndividualBehavior || fallbackText
      case 'team':
        return measure.guideTeamImpact || fallbackText
      case 'organization':
        return measure.guideOrganizationalUse || fallbackText
      case 'personal':
        return measure.guidePersonalDevelopment || fallbackText
      default:
        return fallbackText
    }
  }

  const getSectionTitle = (sectionId: string) => {
    switch (sectionId) {
      case 'definition':
        return 'Measure Definition'
      case 'individual':
        return 'How This Shows Up for Individuals'
      case 'team':
        return 'How This Matters on Teams'
      case 'organization':
        return 'What You Can Do with This Measure for Your Organization'
      case 'personal':
        return 'What You Can Do for Yourself'
      default:
        return ''
    }
  }

  // Check if the current section has content
  const hasContent = (sectionId: string): boolean => {
    switch (sectionId) {
      case 'definition':
        return !!(measure.guideDefinition || measure.description)
      case 'individual':
        return !!measure.guideIndividualBehavior
      case 'team':
        return !!measure.guideTeamImpact
      case 'organization':
        return !!measure.guideOrganizationalUse
      case 'personal':
        return !!measure.guidePersonalDevelopment
      default:
        return false
    }
  }

  return (
    <div className="relative bg-base-white w-full h-[100dvh] flex flex-row items-start justify-start font-text-md-regular">
      <Sidebar />
      <div className="w-full h-[100dvh] flex-1 flex flex-col">
        <HeaderSection />
        <div className="bg-gray-50 relative box-border w-full max-w-[100dvw] h-full flex-1 flex flex-col justify-start py-6 px-9 gap-[19px] text-left text-sm text-gray-600 font-text-sm-medium border-r-[1px] border-solid border-gray-200 overflow-auto">
          <div className="max-w-6xl mx-auto w-full">
            {/* Page Header */}
            <div className="mb-10">
              <button
                onClick={() => navigate('/microlearning')}
                className="text-gray-400 hover:text-gray-600 transition-colors text-sm mb-4 block"
              >
                ← Back to Dashboard
              </button>
              <div className="flex items-center gap-4">
                <img
                  src={getMeasureIconPathByName(measure.measureName)}
                  alt={`${measure.measureName} icon`}
                  className="w-12 h-12"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/v3/assets/measures/8. Communication.png'
                  }}
                />
                <div>
                  <h1 className="text-4xl font-bold text-gray-900">{measure.measureName} Guide</h1>
                  <p className="text-lg text-gray-500 mt-3">Comprehensive guide and development insights</p>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="mb-8">
              <div className="w-full">
                <div className="flex w-full bg-white rounded-xl shadow-sm border border-gray-200 p-1">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`flex-1 px-3 py-3 rounded-md font-medium transition-all duration-200 text-center ${
                        activeSection === section.id
                          ? 'bg-[#4d86ff] text-white shadow-md'
                          : 'text-gray-600 hover:text-gray-900 bg-transparent hover:bg-blue-50'
                      }`}
                    >
                      <span className="flex items-center justify-center gap-2">
                        <SectionIcon sectionId={section.id} className="w-5 h-5" />
                        <span>{section.label}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="text-blue-600">
                    <SectionIcon sectionId={activeSection} className="w-8 h-8" />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {getSectionTitle(activeSection)}
                  </h2>
                </div>

                <div className="prose prose-lg max-w-none">
                  {hasContent(activeSection) ? (
                    <p className="text-gray-700 leading-relaxed text-lg">
                      {getSectionContent(activeSection)}
                    </p>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                      </div>
                      <p className="text-gray-500 text-lg">
                        Guide content for this section is being developed.
                      </p>
                      <p className="text-gray-400 text-sm mt-2">
                        Check back soon for detailed insights about {measure.measureName}.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation Footer */}
              <div className="border-t border-gray-200 px-8 py-6 bg-gray-50 rounded-b-xl">
                <div className="flex justify-between items-center">
                  <button
                    onClick={() => {
                      const currentIndex = sections.findIndex(s => s.id === activeSection)
                      if (currentIndex > 0) {
                        setActiveSection(sections[currentIndex - 1].id)
                      }
                    }}
                    disabled={sections.findIndex(s => s.id === activeSection) === 0}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    ← Previous Section
                  </button>

                  <div className="text-sm text-gray-500">
                    {sections.findIndex(s => s.id === activeSection) + 1} of {sections.length}
                  </div>

                  <button
                    onClick={() => {
                      const currentIndex = sections.findIndex(s => s.id === activeSection)
                      if (currentIndex < sections.length - 1) {
                        setActiveSection(sections[currentIndex + 1].id)
                      }
                    }}
                    disabled={sections.findIndex(s => s.id === activeSection) === sections.length - 1}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next Section →
                  </button>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-8 bg-blue-50 rounded-xl border border-blue-200 p-6">
              <h3 className="font-semibold text-blue-900 mb-3">Ready to Develop This Measure?</h3>
              <p className="text-blue-800 mb-4">Start your personalized learning journey with journal prompts and practice scenarios.</p>
              <button
                onClick={() => navigate('/microlearning')}
                className="px-4 py-2 bg-[#4d86ff] text-white rounded-lg hover:bg-[#3d76ef] transition-colors font-medium"
              >
                Go to Learning Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MeasureGuide
